"use client"

import React from "react"
import { motion } from "framer-motion"
import { Linkedin, Mail, BookOpen, ExternalLink } from "lucide-react"

const CommunityTree = () => {
    const links = [
        {
            id: "linkedin",
            label: "Linkedin",
            icon: <Linkedin className="w-5 h-5" />,
            href: "https://www.linkedin.com/in/rakeshgohel01/",
            position: "top", // top branch
        },
        {
            id: "newsletter",
            label: "Newsletter",
            icon: <Mail className="w-5 h-5" />,
            href: "https://rakeshgohel.substack.com",
            position: "center", // straight end
        },
        {
            id: "cohort",
            label: "Agentic AI Cohort",
            icon: <BookOpen className="w-5 h-5" />,
            href: "https://maven.com/rakeshgohel/ai-agent-engineering-react-rag-multi-agent",
            position: "bottom", // bottom branch
        },
    ]

    return (
        <section className="relative w-full bg-[#020617] py-4 overflow-hidden">
            <div className="max-w-[1800px] mx-auto px-8 lg:px-12 relative z-10">
                <div className="relative bg-[#020617] border border-white/5 rounded-[40px] p-6 lg:py-6 lg:px-12 overflow-hidden shadow-2xl group">

                    {/* Background Glows */}
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-600/20 via-transparent to-transparent opacity-50 pointer-events-none" />
                    <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-500/10 blur-[100px] rounded-full pointer-events-none" />

                    <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] items-center relative z-10">

                        {/* Left Side: Heading */}
                        <div className="max-w-md ">
                            <motion.h2
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                                className="text-4xl md:text-5xl lg:text-7xl font-extrabold text-white leading-[1.1] tracking-tight font-[family-name:var(--font-plus-jakarta-sans)]"
                            >
                                Building a <br />
                                <span className="whitespace-nowrap">community of</span> <br />
                                Learners
                            </motion.h2>
                        </div>

                        {/* Right Side: Tree Structure */}
                        <div className="relative h-auto lg:h-[260px] flex flex-col lg:block items-start mt-8 lg:mt-0 ml-0 lg:-ml-16 overflow-visible">

                            {/* SVG Tree Paths */}
                            <svg className="hidden lg:block absolute inset-0 w-full h-full overflow-visible pointer-events-none" viewBox="0 0 800 260">
                                <defs>
                                    <linearGradient id="centerGradient" gradientUnits="userSpaceOnUse" x1="-33" y1="130" x2="690" y2="130">
                                        <stop offset="0%" stopColor="white" stopOpacity="0.5" />
                                        <stop offset="80%" stopColor="white" stopOpacity="0.5" />
                                        <stop offset="100%" stopColor="white" stopOpacity="0" />
                                    </linearGradient>
                                    <linearGradient id="topGradient" gradientUnits="userSpaceOnUse" x1="180" y1="130" x2="360" y2="40">
                                        <stop offset="0%" stopColor="white" stopOpacity="0.5" />
                                        <stop offset="70%" stopColor="white" stopOpacity="0.5" />
                                        <stop offset="100%" stopColor="white" stopOpacity="0" />
                                    </linearGradient>
                                    <linearGradient id="bottomGradient" gradientUnits="userSpaceOnUse" x1="320" y1="130" x2="500" y2="220">
                                        <stop offset="0%" stopColor="white" stopOpacity="0.5" />
                                        <stop offset="70%" stopColor="white" stopOpacity="0.5" />
                                        <stop offset="100%" stopColor="white" stopOpacity="0" />
                                    </linearGradient>
                                </defs>

                                {/* Extension for Newsletter & Base Stem */}
                                <motion.path
                                    d="M -33 130 L 690 130"
                                    fill="none"
                                    stroke="url(#centerGradient)"
                                    strokeWidth="2"
                                    initial={{ pathLength: 0 }}
                                    whileInView={{ pathLength: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 1.5, ease: "easeInOut" }}
                                />

                                {/* Top Branch (Linkedin) */}
                                <motion.path
                                    d="M 180 130 C 240 130, 240 40, 320 40 L 360 40"
                                    fill="none"
                                    stroke="url(#topGradient)"
                                    strokeWidth="2"
                                    initial={{ pathLength: 0 }}
                                    whileInView={{ pathLength: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 1.2, delay: 0.4, ease: "easeInOut" }}
                                />

                                {/* Bottom Branch (Cohort) */}
                                <motion.path
                                    d="M 320 130 C 380 130, 380 220, 460 220 L 500 220"
                                    fill="none"
                                    stroke="url(#bottomGradient)"
                                    strokeWidth="2"
                                    initial={{ pathLength: 0 }}
                                    whileInView={{ pathLength: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 1.2, delay: 0.6, ease: "easeInOut" }}
                                />
                            </svg>

                            {/* Link Items Wrapper */}
                            <div className="relative w-full h-auto lg:h-full flex flex-col gap-6 lg:block pl-2 lg:pl-0">

                                {/* LinkedIn Link (Top) */}
                                <motion.a
                                    href={links[0].href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    initial={{ opacity: 0, x: -10 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    whileHover={{ x: 5, color: "#fff" }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 1 }}
                                    className="relative lg:absolute lg:top-[26px] lg:left-[370px] flex items-center gap-3 text-white hover:text-white transition-colors group/link"
                                >
                                    <div className="text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]">{links[0].icon}</div>
                                    <span className="text-2xl font-bold font-[family-name:var(--font-plus-jakarta-sans)]">{links[0].label}</span>
                                    <ExternalLink className="w-4 h-4 opacity-50 text-white" />
                                </motion.a>

                                {/* Newsletter Link (Center) */}
                                <motion.a
                                    href={links[1].href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    initial={{ opacity: 0, x: -10 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    whileHover={{ x: 5, color: "#fff" }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.8 }}
                                    className="relative lg:absolute lg:top-[116px] lg:left-[700px] flex items-center gap-3 text-white hover:text-white transition-colors group/link"
                                >
                                    <div className="text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]">{links[1].icon}</div>
                                    <span className="text-2xl font-bold font-[family-name:var(--font-plus-jakarta-sans)]">{links[1].label}</span>
                                    <ExternalLink className="w-4 h-4 opacity-50 text-white" />
                                </motion.a>

                                {/* Cohort Link (Bottom) */}
                                <motion.a
                                    href={links[2].href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    initial={{ opacity: 0, x: -10 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    whileHover={{ x: 5, color: "#fff" }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 1.2 }}
                                    className="relative lg:absolute lg:top-[206px] lg:left-[510px] flex items-center gap-3 text-white hover:text-white transition-colors group/link"
                                >
                                    <div className="text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]">{links[2].icon}</div>
                                    <span className="text-2xl font-bold font-[family-name:var(--font-plus-jakarta-sans)]">{links[2].label}</span>
                                    <ExternalLink className="w-4 h-4 opacity-50 text-white" />
                                </motion.a>

                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </section>
    )
}

export default CommunityTree
